/**
 * Fit Bangla - Global Buy It Now & Cart Checkout Interceptor
 *
 * Intercepts clicks on:
 * 1. "Buy It Now" / Dynamic Checkout buttons across the store (PDP, Featured Product, Cards, Sticky Bar)
 * 2. "Checkout" buttons in Cart Drawer and Cart Page
 * and redirects to customizable checkout URL (e.g. /apps/esolver-cod-form/checkout) instead of Shopify's default checkout.
 */
(function() {
  'use strict';

  var BUY_NOW_SELECTOR = [
    '.shopify-payment-button__button',
    '.fit-featured-product__button--buy-now',
    '.fit-custom-product-card__btn--buy-now',
    '.sticky-buy-now-btn',
    'button[name="buy"]',
    '[data-buy-now]'
  ].join(', ');

  var CART_CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    '#checkout',
    '.cart__checkout-button',
    '.cart__checkout',
    'a[href="/checkout"]',
    'a[href^="/checkout"]',
    '[data-cart-checkout]'
  ].join(', ');

  function getSettings() {
    var config = (window.FitBanglaSettings && window.FitBanglaSettings.buyNow) || {};
    return {
      enabled: config.redirectEnabled !== false,
      customUrl: (config.customUrl || '/apps/esolver-cod-form/checkout').trim(),
      clearCartFirst: !!config.clearCartFirst
    };
  }

  function resolveDestinationUrl(customUrl) {
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = customUrl || '/apps/esolver-cod-form/checkout';
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      if (!url.startsWith('/')) {
        url = '/' + url;
      }
      if (rootUrl !== '/' && url.startsWith('/') && !url.startsWith(rootUrl)) {
        url = rootUrl.replace(/\/$/, '') + url;
      }
    }
    return url;
  }

  function handleCartCheckoutClick(event) {
    var target = event.target;
    var button = target.closest ? target.closest(CART_CHECKOUT_SELECTOR) : null;
    if (!button) return;

    var settings = getSettings();
    if (!settings.enabled) return;

    // Do nothing if button is disabled (e.g., cart is empty)
    if (button.disabled || button.getAttribute('aria-disabled') === 'true' || button.classList.contains('disabled')) {
      return;
    }

    // Stop normal Shopify checkout navigation / form submit
    event.preventDefault();
    event.stopImmediatePropagation();

    if (button.dataset.processing === 'true') {
      return;
    }
    button.dataset.processing = 'true';
    button.disabled = true;
    button.style.opacity = '0.6';
    button.style.cursor = 'wait';

    var destination = resolveDestinationUrl(settings.customUrl);
    window.location.href = destination;
  }

  async function handleBuyNowClick(event) {
    var target = event.target;
    var button = target.closest ? target.closest(BUY_NOW_SELECTOR) : null;
    if (!button) return;

    var settings = getSettings();
    if (!settings.enabled) return;

    // Stop Shopify's default dynamic checkout or browser native action
    event.preventDefault();
    event.stopImmediatePropagation();

    if (button.dataset.processing === 'true' || button.disabled) {
      return;
    }

    // Set loading / processing state
    button.dataset.processing = 'true';
    var prevDisabled = button.disabled;
    var prevOpacity = button.style.opacity;
    var prevCursor = button.style.cursor;
    button.disabled = true;
    button.style.opacity = '0.6';
    button.style.cursor = 'wait';

    try {
      // 1. Locate enclosing form or component
      var form = button.closest('form') ||
                 (button.closest('product-form-component') && button.closest('product-form-component').querySelector('form')) ||
                 (button.closest('product-card') && button.closest('product-card').querySelector('form'));

      var stickyPromo = button.closest('fit-sticky-promo');
      var formData = form ? new FormData(form) : new FormData();

      // Ensure variant id is populated
      var variantId = formData.get('id');
      if (!variantId && stickyPromo && stickyPromo.currentVariantId) {
        variantId = stickyPromo.currentVariantId;
        formData.set('id', variantId);
      }
      if (!variantId && button.dataset.variantId) {
        variantId = button.dataset.variantId;
        formData.set('id', variantId);
      }
      if (!variantId && form) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.value) {
          variantId = idInput.value;
          formData.set('id', variantId);
        }
      }

      // Ensure quantity
      var quantity = formData.get('quantity');
      if (!quantity && stickyPromo) {
        var qtyInput = stickyPromo.querySelector('input[ref="quantityInput"]');
        if (qtyInput && qtyInput.value) {
          quantity = qtyInput.value;
          formData.set('quantity', quantity);
        }
      }
      if (!quantity) {
        formData.set('quantity', '1');
      }

      if (!formData.get('id')) {
        throw new Error('Product variant ID could not be found.');
      }

      // 2. Optionally clear previous cart items
      if (settings.clearCartFirst) {
        try {
          await fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json' }
          });
        } catch (clearErr) {
          console.warn('[FitBangla] Could not clear cart before buy now:', clearErr);
        }
      }

      // 3. Add current item to cart
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        var errJson = await response.json().catch(function() { return {}; });
        throw new Error(errJson.description || errJson.message || 'Error adding product to cart.');
      }

      // 4. Redirect to custom checkout URL
      var destination = resolveDestinationUrl(settings.customUrl);
      window.location.href = destination;

    } catch (err) {
      console.error('[FitBangla] Buy Now Redirect error:', err);
      button.dataset.processing = 'false';
      button.disabled = prevDisabled;
      button.style.opacity = prevOpacity;
      button.style.cursor = prevCursor;
      alert(err.message || 'Could not process Buy Now. Please try again.');
    }
  }

  // Intercept cart form submit as well (e.g. if submitted by enter key)
  function handleFormSubmit(event) {
    var form = event.target;
    if (!form || !form.matches) return;
    if (form.id === 'cart-form' || (form.action && form.action.indexOf('/cart') !== -1)) {
      var submitter = event.submitter;
      if (submitter && submitter.name === 'checkout') {
        var settings = getSettings();
        if (settings.enabled) {
          event.preventDefault();
          event.stopImmediatePropagation();
          window.location.href = resolveDestinationUrl(settings.customUrl);
        }
      }
    }
  }

  function init() {
    document.addEventListener('click', handleBuyNowClick, true);
    document.addEventListener('click', handleCartCheckoutClick, true);
    document.addEventListener('submit', handleFormSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
